import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Container,
    Grid,
    Snackbar,
    Typography
} from "@mui/material";
import { ShoppingCart as ShoppingCartIcon } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import CartDrawer from "../components/shop/CartDrawer";
import { getShopProducts } from "../api/shopApi";
import { formatCurrency } from "../utils/currency";

const SHOP_CART_KEY = "shop_cart_items";
const SHOP_AFFILIATE_KEY = "shop_affiliate_context";

const ShopCheckoutPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const affiliateFromRoute = location.state?.affiliate || null;
    const [affiliate, setAffiliate] = useState(() => {
        if (affiliateFromRoute?.id) return affiliateFromRoute;
        const saved = localStorage.getItem(SHOP_AFFILIATE_KEY);
        try {
            return saved ? JSON.parse(saved) : null;
        } catch (error) {
            return null;
        }
    });

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [cartOpen, setCartOpen] = useState(false);
    const [cartNotice, setCartNotice] = useState({ open: false, message: "", key: 0 });
    const [cart, setCart] = useState(() => {
        const saved = localStorage.getItem(SHOP_CART_KEY);
        try {
            return saved ? JSON.parse(saved) : [];
        } catch (err) {
            return [];
        }
    });

    useEffect(() => {
        if (affiliateFromRoute?.id) {
            setAffiliate(affiliateFromRoute);
            localStorage.setItem(SHOP_AFFILIATE_KEY, JSON.stringify(affiliateFromRoute));
        }
    }, [affiliateFromRoute]);

    useEffect(() => {
        localStorage.setItem(SHOP_CART_KEY, JSON.stringify(cart));
    }, [cart]);

    useEffect(() => {
        const load = async () => {
            if (!affiliate?.id) {
                setLoading(false);
                setError("Affiliate context is missing. Open Shop from Register Training page.");
                return;
            }

            setLoading(true);
            setError("");
            try {
                const productsResponse = await getShopProducts(affiliate.id);
                setProducts(productsResponse.products || []);
            } catch (loadError) {
                console.error("Shop load error:", loadError);
                setError(loadError?.response?.data?.message || "Failed to load shop");
            } finally {
                setLoading(false);
            }
        };

        void load();
    }, [affiliate?.id]);

    const subtotal = useMemo(
        () => cart.reduce((sum, item) => sum + item.finalPrice * item.quantity, 0),
        [cart]
    );
    const paymentCurrency = affiliate?.paymentCurrency || "EUR";
    const formatMoney = (value) => formatCurrency(value, paymentCurrency);

    const handleAddToCart = (product) => {
        if (product.stockQuantity <= 0) return;

        let noticeMessage = `${product.name} added to cart`;
        setCart((prev) => {
            const existing = prev.find((item) => item.productId === product.id);
            if (existing) {
                if (existing.quantity >= product.stockQuantity) {
                    noticeMessage = `${product.name} is already at max stock in cart`;
                    return prev;
                }
                noticeMessage = `${product.name} quantity updated in cart`;
                return prev.map((item) => item.productId === product.id
                    ? { ...item, quantity: item.quantity + 1 }
                    : item
                );
            }
            return [
                ...prev,
                {
                    productId: product.id,
                    name: product.name,
                    quantity: 1,
                    stockQuantity: product.stockQuantity,
                    finalPrice: product.finalPrice,
                    giftCardMeta: product.giftCardMeta || null
                }
            ];
        });
        setCartNotice((prev) => ({
            open: true,
            message: noticeMessage,
            key: prev.key + 1
        }));
    };

    const handleRemoveFromCart = (productId) => {
        setCart((prev) => prev.filter((item) => item.productId !== productId));
    };

    const handleQuantityChange = (productId, quantity) => {
        setCart((prev) => prev.map((item) => {
            if (item.productId !== productId) return item;
            const nextQty = Number.isFinite(quantity) ? quantity : item.quantity;
            const safeQty = Math.max(1, Math.min(nextQty, item.stockQuantity));
            return { ...item, quantity: safeQty };
        }));
    };

    const handleGoToCheckout = () => {
        if (!cart.length) return;
        navigate("/shop-checkout/checkout", {
            state: {
                affiliate,
                cart
            }
        });
    };

    return (
        <Container maxWidth="lg" sx={{ py: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2, flexWrap: "wrap" }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {affiliate?.name ? `${affiliate.name} Shop` : "Shop"}
                    </Typography>
                    <Typography color="text.secondary">
                        Buy products directly from your club.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<ShoppingCartIcon />}
                    onClick={() => setCartOpen(true)}
                    disabled={!cart.length}
                >
                    Cart ({cart.reduce((sum, item) => sum + item.quantity, 0)})
                </Button>
            </Box>

            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

            <Grid container spacing={2}>
                {loading ? (
                    <Grid item xs={12}>
                        <Typography>Loading products...</Typography>
                    </Grid>
                ) : (
                    products.map((product) => (
                        <Grid item xs={12} sm={6} md={4} key={product.id}>
                            <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                                {(product.imageData || product.imageUrl) ? (
                                    <CardMedia
                                        component="img"
                                        height="180"
                                        image={product.imageData || product.imageUrl}
                                        alt={product.name}
                                    />
                                ) : null}
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography variant="h6">{product.name}</Typography>
                                    {product.giftCardMeta ? (
                                        <Typography variant="caption" color="secondary" sx={{ display: "block" }}>
                                            Gift card
                                        </Typography>
                                    ) : null}
                                    <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>
                                        {product.description || "No product description"}
                                    </Typography>
                                    {product.giftCardMeta ? (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                            Validity: {product.giftCardMeta.validityDays} days
                                            {product.giftCardMeta.redemptionMode === "plan" ? " | Redeems a plan" : " | Redeems as credit"}
                                        </Typography>
                                    ) : null}
                                    <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                        {product.discountPrice ? (
                                            <>
                                                <Typography sx={{ textDecoration: "line-through" }} color="text.secondary">
                                                    {formatMoney(product.price)}
                                                </Typography>
                                                <Typography sx={{ fontWeight: 700 }}>
                                                    {formatMoney(product.finalPrice)}
                                                </Typography>
                                            </>
                                        ) : (
                                            <Typography sx={{ fontWeight: 700 }}>{formatMoney(product.price)}</Typography>
                                        )}
                                    </Box>
                                    {!product.giftCardMeta ? (
                                        <Typography variant="caption" color={product.stockQuantity > 0 ? "text.secondary" : "error"} sx={{ display: "block", mt: 0.5 }}>
                                            Stock: {product.stockQuantity}
                                        </Typography>
                                    ) : null}
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        sx={{ mt: 1.5 }}
                                        onClick={() => handleAddToCart(product)}
                                        disabled={product.stockQuantity <= 0}
                                    >
                                        {product.stockQuantity <= 0 ? "Out of stock" : (product.giftCardMeta ? "Buy gift card" : "Add to cart")}
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                )}
            </Grid>

            <CartDrawer
                open={cartOpen}
                onClose={() => setCartOpen(false)}
                cartItems={cart}
                onRemove={handleRemoveFromCart}
                onQuantityChange={handleQuantityChange}
                subtotal={subtotal}
                onCheckout={handleGoToCheckout}
                checkoutLabel="Go to checkout"
                paymentCurrency={paymentCurrency}
            />

            {/* Bottom spacer for phone + desktop menu bars */}
            <Box sx={{ height: { xs: "calc(90px + env(safe-area-inset-bottom, 0px))", md: 88 } }} />

            <Snackbar
                key={cartNotice.key}
                open={cartNotice.open}
                autoHideDuration={2000}
                onClose={() => setCartNotice((prev) => ({ ...prev, open: false }))}
                message={cartNotice.message}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                sx={{ mt: { xs: 8, md: 2 } }}
            />
        </Container>
    );
};

export default ShopCheckoutPage;
